import {
  Container,
  Paper,
  Stack,
  Title,
  Text,
  Group,
  Box,
  Badge,
  Divider,
  Card,
  Progress,
  Grid,
  Center,
  ThemeIcon
} from "@mantine/core";
import { IconTrophy, IconTarget, IconTrendingUp, IconAward } from "@tabler/icons-react";
import { themeTokens } from "@/src/theme/tokens";

// Constants
const PROGRESS_MULTIPLIER = 10;
const premiumGradient = `linear-gradient(135deg, ${themeTokens.colors.premiumAqua} 0%, ${themeTokens.colors.premiumPink} 50%, ${themeTokens.colors.premiumOrange} 100%)`;
const surfaceGradient = `linear-gradient(45deg, ${themeTokens.colors.surface}, ${themeTokens.colors.surfaceMuted})`;
const footerGradient = `linear-gradient(135deg, ${themeTokens.palette.neutral[8]}, ${themeTokens.palette.textSecondary[6]})`;

const PremiumPage: React.FC = () => {
  return (
    <Box style={{ minHeight: "100vh", backgroundColor: themeTokens.colors.surface }}>
      <Container size="xl" px={0}>
        <Stack>
          {/* Header Section */}
          <Paper
            p="xl"
            style={{
              background: premiumGradient,
              color: "white",
              textAlign: "center",
              position: "relative",
              overflow: "hidden"
            }}
          >
            <Box style={{ position: "relative", zIndex: 2 }}>
              <Group justify="center" mb="lg">
                <ThemeIcon size={60} radius="xl" variant="light" color="white">
                  <IconTrophy size={40} />
                </ThemeIcon>
              </Group>
              <Title order={1} size="h1" fw={700} mb="md">
                Prêmio Empresa Inovadora 2024
              </Title>
              <Text size="xl" mb="md">
                Certificado de Excelência em Inovação
              </Text>
              <Badge size="lg" variant="light" color="white" style={{ backgroundColor: "rgba(255,255,255,0.2)" }}>
                Template Premium
              </Badge>
            </Box>
            {/* Decorative elements */}
            <Box
              style={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                background: "rgba(255,255,255,0.1)",
                borderRadius: "50%",
                zIndex: 1
              }}
            />
            <Box
              style={{
                position: "absolute",
                bottom: -30,
                left: -30,
                width: 150,
                height: 150,
                background: "rgba(255,255,255,0.1)",
                borderRadius: "50%",
                zIndex: 1
              }}
            />
          </Paper>

          {/* Content Container */}
          <Container size="lg" py="xl">
            <Stack>
              {/* Section 1 - Introdução */}
              <Card shadow="md" p="xl" radius="lg">
                <Group mb="lg">
                  <ThemeIcon size={40} radius="md" color="premiumAqua">
                    <IconAward size={24} />
                  </ThemeIcon>
                  <Title order={2} c={themeTokens.colors.premiumAqua}>
                    Certificação de Empresa Inovadora
                  </Title>
                </Group>
                <Text size="lg" c="dimmed" mb="md">
                  Este documento certifica que a empresa atende aos critérios de inovação estabelecidos pelo programa de
                  aceleração de inovação (ADI).
                </Text>
                <Divider my="md" />
                <Text size="sm" c="dimmed">
                  Certificação baseada em metodologia internacional de avaliação de maturidade em inovação
                </Text>
              </Card>

              {/* Section 2 - Ranking Cards */}
              <Grid>
                <Grid.Col span={6}>
                  <Card shadow="md" p="lg" radius="lg" style={{ height: "100%" }}>
                    <Group mb="md">
                      <ThemeIcon size={40} radius="md" color="premiumPink">
                        <IconTrendingUp size={24} />
                      </ThemeIcon>
                      <Box>
                        <Title order={3} c={themeTokens.colors.premiumPink} size="h4">
                          Posicionamento Geral
                        </Title>
                        <Text size="sm" c="dimmed">
                          Ranking nacional
                        </Text>
                      </Box>
                    </Group>
                    <Center>
                      <Box style={{ textAlign: "center" }}>
                        <Text size="48px" fw={700} c={themeTokens.colors.premiumPink} mb={0} style={{ lineHeight: 1 }}>
                          #15
                        </Text>
                        <Text size="sm" c="dimmed">
                          Entre 147 empresas
                        </Text>
                      </Box>
                    </Center>
                  </Card>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Card shadow="md" p="lg" radius="lg" style={{ height: "100%" }}>
                    <Group mb="md">
                      <ThemeIcon size={40} radius="md" color="premiumOrange">
                        <IconTarget size={24} />
                      </ThemeIcon>
                      <Box>
                        <Title order={3} c={themeTokens.colors.premiumOrange} size="h4">
                          Categoria Específica
                        </Title>
                        <Text size="sm" c="dimmed">
                          Inovação Tecnológica
                        </Text>
                      </Box>
                    </Group>
                    <Center>
                      <Box style={{ textAlign: "center" }}>
                        <Text
                          size="48px"
                          fw={700}
                          c={themeTokens.colors.premiumOrange}
                          mb={0}
                          style={{ lineHeight: 1 }}
                        >
                          3º
                        </Text>
                        <Text size="sm" c="dimmed">
                          Lugar na categoria
                        </Text>
                      </Box>
                    </Center>
                  </Card>
                </Grid.Col>
              </Grid>

              {/* Section 3 - Gráfico de Desempenho */}
              <Card shadow="md" p="xl" radius="lg">
                <Title order={3} mb="lg" c={themeTokens.colors.premiumAqua}>
                  Gráfico I: Desempenho da Empresa
                </Title>
                <Grid>
                  <Grid.Col span={6}>
                    <Box
                      style={{
                        height: 300,
                        background: surfaceGradient,
                        borderRadius: 12,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: `2px dashed ${themeTokens.colors.premiumAqua}`,
                        position: "relative"
                      }}
                    >
                      <Box style={{ textAlign: "center" }}>
                        <ThemeIcon size={60} radius="xl" color="premiumAqua" mb="md">
                          <IconTarget size={30} />
                        </ThemeIcon>
                        <Text c={themeTokens.colors.premiumAqua} fw={500}>
                          Gráfico Radar - Desempenho
                        </Text>
                        <Text size="xs" c="dimmed" mt="xs">
                          Análise comparativa AA vs AE
                        </Text>
                      </Box>
                    </Box>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Box pl="md">
                      <Badge size="lg" variant="light" color="premiumAqua" mb="md">
                        Análise Comparativa
                      </Badge>
                      <Text size="sm" fw={500} mb="md">
                        Autoavaliação (AA) versus Avaliação Externa (AE)
                      </Text>
                      <Text size="sm" c="dimmed" mb="lg">
                        O gráfico apresenta uma análise comparativa entre a autoavaliação realizada pela empresa e a
                        avaliação externa conduzida por especialistas. Esta comparação permite identificar pontos de
                        convergência e divergência na percepção do nível de maturidade em inovação.
                      </Text>
                      <Group gap="xs">
                        <Badge variant="dot" color="premiumAqua">
                          AA
                        </Badge>
                        <Badge variant="dot" color="premiumPink">
                          AE
                        </Badge>
                      </Group>
                    </Box>
                  </Grid.Col>
                </Grid>
              </Card>

              {/* Section 4 - Critérios de Avaliação */}
              <Card shadow="md" p="xl" radius="lg">
                <Title order={3} mb="lg" c={themeTokens.colors.premiumOrange}>
                  Critérios de Avaliação
                </Title>
                <Stack gap="lg">
                  {[
                    { label: "Cultura de Inovação", value: 8.5, color: themeTokens.colors.premiumAqua },
                    { label: "Processos de Inovação", value: 7.8, color: themeTokens.colors.premiumPink },
                    { label: "Resultados em Inovação", value: 9.2, color: themeTokens.colors.premiumOrange },
                    { label: "Impacto no Mercado", value: 8.1, color: themeTokens.colors.premiumAqua },
                    { label: "Sustentabilidade", value: 7.9, color: themeTokens.colors.premiumPink }
                  ].map((criterion, index) => (
                    <Box key={index}>
                      <Group justify="apart" mb="xs">
                        <Text fw={600} size="sm">
                          {criterion.label}
                        </Text>
                        <Text fw={700} c={criterion.color}>
                          {criterion.value}/10
                        </Text>
                      </Group>
                      <Progress
                        value={criterion.value * PROGRESS_MULTIPLIER}
                        color={criterion.color}
                        size="md"
                        radius="xl"
                      />
                    </Box>
                  ))}
                </Stack>
              </Card>

              {/* Section 5 - Recomendações */}
              <Card shadow="md" p="xl" radius="lg">
                <Title order={3} mb="lg" c={themeTokens.colors.premiumAqua}>
                  Recomendações para Melhoria
                </Title>
                <Text mb="lg" c="dimmed">
                  Com base na avaliação realizada, recomendamos as seguintes ações para fortalecer ainda mais o
                  ecossistema de inovação da empresa:
                </Text>
                <Stack gap="md">
                  {[
                    "Implementar programa estruturado de ideação colaborativa",
                    "Estabelecer parcerias estratégicas com universidades",
                    "Desenvolver métricas específicas para acompanhamento de projetos",
                    "Investir em capacitação da equipe em metodologias ágeis",
                    "Criar laboratório de prototipagem rápida"
                  ].map((recommendation, index) => (
                    <Group key={index} align="flex-start" gap="md">
                      <ThemeIcon size={24} radius="xl" color="premiumAqua" variant="light" style={{ marginTop: 2 }}>
                        <Text size="xs" fw={700}>
                          {index + 1}
                        </Text>
                      </ThemeIcon>
                      <Text size="sm">{recommendation}</Text>
                    </Group>
                  ))}
                </Stack>
              </Card>
            </Stack>
          </Container>

          {/* Footer */}
          <Paper
            p="xl"
            style={{
              background: footerGradient,
              color: "white",
              textAlign: "center",
              marginTop: 40
            }}
          >
            <Group justify="center" mb="md">
              <ThemeIcon size={40} radius="xl" color="premiumAqua">
                <IconAward size={24} />
              </ThemeIcon>
            </Group>
            <Text size="lg" fw={600} mb="xs">
              Certificado emitido pelo Acelerador de Inovação (ADI)
            </Text>
            <Text size="sm" c="dimmed" mb="md">
              Data de emissão: {new Date().toLocaleDateString("pt-BR")}
            </Text>
            <Divider my="md" color="gray.6" />
            <Text size="xs" c="dimmed">
              Este certificado é válido como comprovação de participação no programa de avaliação de maturidade em
              inovação
            </Text>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
};

export default PremiumPage;
